import merge from 'lodash/merge'
import isEmpty from 'lodash/isEmpty'
import { useRef } from 'react'
import { jsonStringify, jsonTryParse } from '@ztechapps/common.utils'
import type { ConfirmDialogConfig } from '../useConfirmDialog/ConfirmDialogContext'
import { useConfirmDialogContext } from '../useConfirmDialog/ConfirmDialogContext'

export type PromptDialogOptions = ConfirmDialogConfig

export type SetPromptAction<T extends Record<any, any> = any> = (value: string | T) => void

export function usePromptDialog<T extends Record<any, any> = any>(config: PromptDialogOptions = {}) {
  const ctx = useConfirmDialogContext()
  const promptValueRef = useRef<string>('')

  config = merge(
    {
      title: '',
      text: 'Enter a value',
      icon: 'info',
      buttons: {
        cancel: 'Cancel',
        confirm: {
          text: 'OK',
          value: 'OK',
        },
      },
      dangerMode: true,
    },
    config,
  )

  const prompt = (confirmed?: (value: T) => void, cancelled?: () => void, promptConfig?: PromptDialogOptions) => {
    const mergedConfig = merge({}, config, { content: 'input' }, promptConfig)

    promptValueRef.current = ''

    return ctx.showDialog(mergedConfig, promptValueRef).then((value: string) => {
      if (!isEmpty(value) && confirmed) {
        let normalizedValue = jsonTryParse(value)
        normalizedValue = normalizedValue === false ? value : normalizedValue

        return confirmed(normalizedValue)
      } else {
        if (cancelled) {
          return cancelled()
        }
      }
    })
  }

  const setPromptAction: SetPromptAction<T> = (value: string | T = {} as unknown as T) => {
    let normalizedValue = value
    if (typeof value === 'object') {
      normalizedValue = jsonStringify(value)
    }
    promptValueRef.current = normalizedValue as string
  }

  return { prompt, config, setPromptAction }
}
